<template>
  <v-autocomplete
      :key="selectedModel ? selectedModel.id : 'reset'"
      :error-messages="error"
      variant="outlined"
      rounded="0"
      :label="translation(translationList.vehicle_model, 'Model')"
      :items="vehicleModels"
      name="model"
      item-title="name"
      item-value="id"
      :loading="loading"
      :disabled="loading"
      v-model="selectedModel"
      :rules="[v => !!v || translation(translationList.required, 'Acest camp este obligatoriu')]"
      return-object
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" open-on-click open-on-hover>
        <template v-slot:activator="{ props }">
           <v-icon v-bind="props" class="cursor-pointer" aria-label="info">
            mdi-information
          </v-icon>
        </template>
        <span>
          <NuxtImg
              src="/images/vehicle/registration_template/template_d3.png"
              class="h-100"
              loading="lazy"
              alt="Registration template"
          />
        </span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useVehicleStore } from '~/plugins/store/vehicle';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {VehicleModel} from "~/types/Vehicle";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => VehicleModel | null,
      default: () => null,
      required: false
    },
    vehicleMakeId: {
      required: false,
      type: Number as () => number | null,
    },
  },
  emits: ['update:modelValue', 'validation-vehicle-status'],
  setup(props, { emit }) {
    const { modelValue, vehicleMakeId } = toRefs(props);
    const vehicleStore = useVehicleStore();
    const translationStore = useTranslationStore();
    const selectedModel = ref(modelValue.value ? modelValue.value : null);
    const vehicleModels = ref<VehicleModel[]>([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    const error = ref([]);

    const fetchVehicleModels = async (makeId: number) => {
      loading.value = true;
      if (makeId) {
        vehicleModels.value = await vehicleStore.getVehicleModels(makeId);
      } else {
        vehicleModels.value = [];
      }
      loading.value = false;
    };

    onMounted(async () => {
      if (vehicleMakeId.value) {
        await fetchVehicleModels(vehicleMakeId.value);
      }
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-vehicle', (data) => {
        if (data.detail && data.detail === 'model') {
          if (!selectedModel.value) {
            error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
          } else {
            error.value = [];
            emit('validation-vehicle-status', { field: 'model', isValid: true });
          }
        }
      });
    });

    watch(vehicleMakeId, async (newMakeId) => {
      if (modelValue.value && modelValue.value.rca_vehicle_make_id === newMakeId) {
        selectedModel.value = modelValue.value;
      } else {
        selectedModel.value = null;
      }
      emit('update:modelValue', selectedModel.value);
      if (newMakeId) {
        await fetchVehicleModels(newMakeId);
      }
    });

    watch(selectedModel, (newModel) => {
      emit('update:modelValue', newModel);
    });

    watch(modelValue, (newValue) => {
      if (!newValue) {
        selectedModel.value = null;
      } else if (newValue ) {
        selectedModel.value = newValue;
        error.value = [];
      }
      const isValid = !!newValue;
      emit('validation-vehicle-status', { field: 'model', isValid });
    });

    return {
      selectedModel,
      translationList,
      translation,
      vehicleModels,
      loading,
      error,
    };
  },
});
</script>

<style scoped>
</style>
